import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import SearchIcon from "@mui/icons-material/Search";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SyncIcon from "@mui/icons-material/Sync";
import { formatDistanceToNow } from "date-fns";
import { AppContext } from "../Store";
import { useSnackbar } from "../shared/SnackbarProvider";
import { StatStrip } from "../shared/StatStrip";
import {
  HolidayInvoice,
  HolidayRegistration,
  HolidayTerm,
  createHolidayInvoices,
  createHolidayReceipt,
  formatPounds,
  recreateHolidayInvoice,
  today,
  updateInvoice,
  updateRegistrationsAdmin,
  watchInvoices,
  saveTermDates,
  termDayDates,
  watchRegistrations,
  watchTermDates,
  watchTerms,
} from "../shared/holidayClub";
import { TermDatesDialog } from "./holidayClub/TermDatesDialog";
import EventIcon from "@mui/icons-material/Event";
import { Family, FamilyStatus, STATUS_LABEL, STATUS_ORDER, buildFamilies, matchesSearch } from "./holidayClub/families";
import { FamilyCard, FamilyActions } from "./holidayClub/FamilyCard";

const DISCOUNT_REASONS = ["None", "Sibling", "Early Bird"];
const OTHER = "Other";

type Filter = "all" | FamilyStatus;

const HolidayClubPage: React.FC = () => {
  const { userContext } = useContext(AppContext);
  const snack = useSnackbar();

  const [terms, setTerms] = useState<HolidayTerm[] | null>(null);
  const [termId, setTermId] = useState<string | null>(null);
  const [registrations, setRegistrations] = useState<HolidayRegistration[]>([]);
  const [invoices, setInvoices] = useState<HolidayInvoice[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [search, setSearch] = useState("");
  const [confirmCreate, setConfirmCreate] = useState(false);
  const [receiptFor, setReceiptFor] = useState<HolidayInvoice | null>(null);
  const [paymentDate, setPaymentDate] = useState(today());
  const [discountFor, setDiscountFor] = useState<Family | null>(null);
  const [discount, setDiscount] = useState({ choice: "None", custom: "", percentage: "0" });
  const [undo, setUndo] = useState<{ message: string; revert: () => Promise<void> } | null>(null);
  const [termDates, setTermDates] = useState<{ [termId: string]: { [label: string]: string } }>({});
  const [editingDates, setEditingDates] = useState(false);

  const onError = (error: Error) => snack(`Could not load holiday club data: ${error.message}`, "error");

  useEffect(() => watchTermDates(setTermDates, (error) => console.warn("Date corrections not loaded", error)), []);

  useEffect(() => watchTerms((loaded) => {
    setTerms(loaded);
    setTermId((current) => current ?? loaded[0]?.id ?? null);
  }, onError), []);

  useEffect(() => {
    if (!termId) return undefined;
    setRegistrations([]);
    setInvoices([]);
    const stopRegistrations = watchRegistrations(termId, setRegistrations, onError);
    const stopInvoices = watchInvoices(termId, setInvoices, onError);
    return () => {
      stopRegistrations();
      stopInvoices();
    };
  }, [termId]);

  const term = terms?.find((t) => t.id === termId) ?? null;
  const { families, pendingCount } = useMemo(
    () => (term ? buildFamilies(term, registrations, invoices) : { families: [] as Family[], pendingCount: 0 }),
    [term, registrations, invoices]
  );

  // `npm run dev` signs in as a mock teacher and serves mock holiday data, so let it through.
  if (!import.meta.env.DEV && !userContext?.isAdmin()) {
    return <Box sx={{ padding: 2 }}><Alert severity="warning">Only the manager can see holiday club invoicing.</Alert></Box>;
  }

  const run = async (label: string, work: () => Promise<void>) => {
    setBusy(label);
    try {
      await work();
    } catch (error) {
      snack(`${label} failed: ${error instanceof Error ? error.message : String(error)}`, "error");
    } finally {
      setBusy(null);
    }
  };

  /** Saves a quick change straight away and offers to undo it. */
  const quick = async (message: string, apply: () => Promise<void>, revert: () => Promise<void>) => {
    try {
      await apply();
      setUndo({ message, revert });
    } catch (error) {
      snack(`Could not save: ${error instanceof Error ? error.message : String(error)}`, "error");
    }
  };

  const actions: FamilyActions = {
    setPaid: (family, paid) => {
      const invoice = family.invoice!;
      const before = { paid: invoice.paid, paidDate: invoice.paidDate };
      quick(
        `#${invoice.invoiceNo} ${family.parentName} marked ${paid ? "paid" : "unpaid"}`,
        () => updateInvoice(invoice.invoiceNo, { paid, paidDate: paid ? today() : null }),
        () => updateInvoice(invoice.invoiceNo, before),
      );
    },
    setCancelled: (family, cancelled) => {
      const label = `${family.parentName} ${cancelled ? "cancelled" : "restored"}`;
      if (family.invoice) {
        const invoiceNo = family.invoice.invoiceNo;
        quick(label, () => updateInvoice(invoiceNo, { cancelled }), () => updateInvoice(invoiceNo, { cancelled: !cancelled }));
      } else {
        quick(label,
          () => updateRegistrationsAdmin(family.registrationIds, { cancelled }),
          () => updateRegistrationsAdmin(family.registrationIds, { cancelled: !cancelled }));
      }
    },
    editDiscount: (family) => {
      const reason = family.discountReason || "None";
      const preset = DISCOUNT_REASONS.includes(reason);
      setDiscount({ choice: preset ? reason : OTHER, custom: preset ? "" : reason, percentage: String(family.discountPercentage) });
      setDiscountFor(family);
    },
    receipt: (family) => {
      setPaymentDate(family.invoice!.paidDate || today());
      setReceiptFor(family.invoice!);
    },
    recreate: (family) => run(`Recreating invoice #${family.invoice!.invoiceNo}`, async () => {
      const result = await recreateHolidayInvoice(family.invoice!.invoiceNo);
      snack(`Invoice #${result.invoiceNo} recreated (£${formatPounds(result.totalPence)}); new draft in Gmail`, "success");
    }),
  };

  const createInvoices = () => run("Creating invoices", async () => {
    setConfirmCreate(false);
    const result = await createHolidayInvoices(termId!);
    const failed = result.created.filter((c) => c.error);
    snack(
      `Created ${result.created.length} invoice(s)` +
      (failed.length ? `; ${failed.length} without a draft (use Recreate)` : "") +
      (result.problems.length ? `; ${result.problems.length} need attention` : ""),
      failed.length || result.problems.length ? "warning" : "success"
    );
  });

  const createReceipt = () => run("Creating receipt", async () => {
    const invoice = receiptFor!;
    setReceiptFor(null);
    await createHolidayReceipt(invoice.invoiceNo, paymentDate);
    snack(`Receipt for #${invoice.invoiceNo} drafted in Gmail; marked paid`, "success");
  });

  const percentage = Number(discount.percentage);
  const discountReason = discount.choice === OTHER ? discount.custom.trim() : discount.choice;
  const percentageValid = discount.percentage.trim() !== "" && percentage >= 0 && percentage <= 100;
  const discountValid = percentageValid && discountReason !== "";
  const saveDiscount = () => run("Saving discount", async () => {
    const family = discountFor!;
    setDiscountFor(null);
    await updateRegistrationsAdmin(family.registrationIds, {
      discountReason,
      discountPercentage: discountReason === "None" ? 0 : percentage,
    });
  });

  const counts = STATUS_ORDER.reduce((all, status) => ({ ...all, [status]: families.filter((f) => f.status === status).length }),
    {} as Record<FamilyStatus, number>);
  const shown = families.filter((f) => (filter === "all" || f.status === filter) && matchesSearch(f, search));
  const sum = (status: FamilyStatus[]) =>
    families.filter((f) => status.includes(f.status)).reduce((total, f) => total + (f.totalPence ?? 0), 0);
  const invoicedPence = sum(["unpaid", "paid"]);
  const paidPence = sum(["paid"]);
  const toInvoicePence = sum(["toInvoice"]);
  const children = families.filter((f) => f.status !== "cancelled").reduce((total, f) => total + f.children.length, 0);

  return (
    <Box sx={{ px: { xs: 1.5, md: 3 }, py: 2, maxWidth: 1280, mx: "auto" }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }} sx={{ mb: 1 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" fontWeight={600}>Holiday Club</Typography>
          {term && (
            <Typography variant="body2" color="text.secondary">
              {term.days.length} days · £{formatPounds(term.fullDayPricePence)} full day · £{formatPounds(term.halfDayPricePence)} half day
            </Typography>
          )}
        </Box>
        <FormControl size="small" sx={{ minWidth: 300 }}>
          <InputLabel>Term</InputLabel>
          <Select label="Term" value={termId ?? ""} onChange={(e) => setTermId(e.target.value)}>
            {(terms ?? []).map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
          </Select>
        </FormControl>
      </Stack>

      {term && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <Chip size="small" icon={<SyncIcon />} variant="outlined"
            label={`Synced ${formatDistanceToNow(new Date(term.syncedAt), { addSuffix: true })}`} />
          {(() => {
            const days = termDayDates(term, termDates[term.id]);
            const unread = days.filter((d) => !d.date).length;
            const corrected = days.filter((d) => d.corrected).length;
            return (
              <Chip size="small" icon={<EventIcon />} clickable onClick={() => setEditingDates(true)}
                color={unread ? "error" : corrected ? "info" : "default"} variant={unread || corrected ? "filled" : "outlined"}
                label={unread ? `Dates · ${unread} unread` : corrected ? `Dates · ${corrected} corrected` : "Dates"} />
            );
          })()}
          <Chip size="small" icon={<OpenInNewIcon />} label="Form" component="a" href={term.formUrl} target="_blank" clickable />
          {term.sheetUrl && <Chip size="small" icon={<OpenInNewIcon />} label="Responses" component="a" href={term.sheetUrl} target="_blank" clickable />}
          <Chip size="small" icon={<OpenInNewIcon />} label="Gmail drafts" component="a" href="https://mail.google.com/mail/u/0/#drafts" target="_blank" clickable />
        </Stack>
      )}

      {terms === null && <CircularProgress />}
      {terms?.length === 0 && (
        <Alert severity="info">No holiday terms yet. Run installHolidaySyncTriggers in the invoice-generator Apps Script.</Alert>
      )}

      {term && (
        <>
          {termDayDates(term, termDates[term.id]).some((day) => !day.date) && (
            <Alert severity="warning" sx={{ mb: 2 }}
              action={<Button color="inherit" size="small" onClick={() => setEditingDates(true)}>Set dates</Button>}>
              Some day labels on the form could not be read as dates.
            </Alert>
          )}

          <StatStrip items={[
            { label: "Invoiced", color: "primary", value: `£${formatPounds(invoicedPence)}`, hint: `${counts.unpaid + counts.paid} invoices · ${children} children` },
            { label: "Paid", color: "success", value: `£${formatPounds(paidPence)}`,
              hint: invoicedPence ? `${Math.round((paidPence / invoicedPence) * 100)}% collected` : undefined,
              progress: invoicedPence ? (paidPence / invoicedPence) * 100 : 0 },
            { label: "Outstanding", color: "info", value: `£${formatPounds(invoicedPence - paidPence)}`, hint: `${counts.unpaid} unpaid` },
            { label: "To invoice", color: "error", value: `£${formatPounds(toInvoicePence)}`,
              hint: counts.attention ? `${pendingCount} ready · ${counts.attention} need attention` : `${pendingCount} ready`,
              action: (
                <Button variant="contained" size="small" disabled={pendingCount === 0 || !!busy}
                  startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <SendIcon />} onClick={() => setConfirmCreate(true)}>
                  {busy ?? `Create ${pendingCount} invoice${pendingCount === 1 ? "" : "s"}`}
                </Button>
              ) },
          ]} />

          <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }} sx={{ mb: 2 }}>
            <ToggleButtonGroup size="small" exclusive value={filter} onChange={(_, value) => value && setFilter(value)}
              sx={{ flexWrap: "wrap" }}>
              <ToggleButton value="all">All {families.length}</ToggleButton>
              {STATUS_ORDER.filter((status) => counts[status] > 0 || status === filter).map((status) => (
                <ToggleButton key={status} value={status}>{STATUS_LABEL[status]} {counts[status]}</ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Box sx={{ flexGrow: 1 }} />
            <TextField
              size="small"
              placeholder="Search parent, child or #invoice"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 280 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            />
          </Stack>

          <Stack spacing={1}>
            {shown.map((family) => <FamilyCard key={family.id} family={family} busy={!!busy} actions={actions} />)}
            {shown.length === 0 && (
              <Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
                <Typography color="text.secondary">
                  {families.length === 0 ? "No registrations for this term yet." : "Nothing matches."}
                </Typography>
              </Paper>
            )}
          </Stack>
        </>
      )}

      <Dialog open={confirmCreate} onClose={() => setConfirmCreate(false)}>
        <DialogTitle>Create {pendingCount} invoice{pendingCount === 1 ? "" : "s"}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Each family gets the next invoice number and a Gmail draft with the PDF. Nothing is sent: review the drafts
            in Gmail and send them from there.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmCreate(false)}>Cancel</Button>
          <Button variant="contained" onClick={createInvoices}>Create invoices</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!receiptFor} onClose={() => setReceiptFor(null)} fullWidth maxWidth="xs">
        <DialogTitle>Receipt for #{receiptFor?.invoiceNo}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Marks the invoice paid and leaves a receipt draft for {receiptFor?.parentName}
            {receiptFor ? ` (£${formatPounds(receiptFor.totalPence)})` : ""}.
          </DialogContentText>
          <TextField label="Payment date" type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)}
            InputLabelProps={{ shrink: true }} fullWidth />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReceiptFor(null)}>Cancel</Button>
          <Button variant="contained" disabled={!paymentDate} onClick={createReceipt}>Create receipt</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!discountFor} onClose={() => setDiscountFor(null)} fullWidth maxWidth="xs">
        <DialogTitle>Discount for {discountFor?.parentName}</DialogTitle>
        <DialogContent>
          {discountFor && discountFor.children.length > 1 && (
            <DialogContentText sx={{ mb: 2 }}>Applies to {discountFor.children.map((c) => c.name).join(" and ")}.</DialogContentText>
          )}
          <ToggleButtonGroup exclusive fullWidth size="small" value={discount.choice} sx={{ mb: 2, mt: 1 }}
            onChange={(_, choice) => choice && setDiscount({
              ...discount,
              choice,
              percentage: choice === "None" ? "0" : discount.percentage === "0" ? "10" : discount.percentage,
            })}>
            {[...DISCOUNT_REASONS, OTHER].map((reason) => <ToggleButton key={reason} value={reason}>{reason}</ToggleButton>)}
          </ToggleButtonGroup>
          {discount.choice === OTHER && (
            <TextField
              label="Reason"
              placeholder="e.g. Staff child, Goodwill"
              fullWidth
              autoFocus
              sx={{ mb: 2 }}
              value={discount.custom}
              onChange={(e) => setDiscount({ ...discount, custom: e.target.value })}
              helperText="Shown on the invoice"
            />
          )}
          <TextField
            label="Discount"
            type="number"
            fullWidth
            disabled={discount.choice === "None"}
            value={discount.percentage}
            onChange={(e) => setDiscount({ ...discount, percentage: e.target.value })}
            error={!percentageValid}
            helperText={percentageValid ? " " : "Between 0 and 100"}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment>, inputProps: { min: 0, max: 100 } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiscountFor(null)}>Cancel</Button>
          <Button variant="contained" disabled={!discountValid} onClick={saveDiscount}>Save</Button>
        </DialogActions>
      </Dialog>

      <TermDatesDialog term={editingDates ? term : null} corrections={term ? termDates[term.id] ?? {} : {}}
        onClose={() => setEditingDates(false)}
        onSave={async (dates) => {
          try {
            await saveTermDates(term!.id, dates);
            snack("Club dates saved", "success");
          } catch (error) {
            snack(`Could not save dates: ${error instanceof Error ? error.message : String(error)}`, "error");
          }
        }} />

      <Snackbar
        open={!!undo}
        autoHideDuration={6000}
        onClose={(_, reason) => reason !== "clickaway" && setUndo(null)}
        message={undo?.message}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        action={
          <Button color="inherit" size="small" sx={{ fontWeight: 700 }} onClick={() => {
            const revert = undo!.revert;
            setUndo(null);
            revert().catch((error) => snack(`Could not undo: ${error.message}`, "error"));
          }}>
            Undo
          </Button>
        }
      />
    </Box>
  );
};

export default HolidayClubPage;
